import { Checkbox, Stepper } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    IconCheck,
    IconClock,
    IconExclamationCircle,
    IconShieldCheck,
    IconUpload,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Compressor from 'compressorjs';
import { ChangeEvent, DragEvent, Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { KYCLevel, KYCStatus, User } from '@xver/@types/user';
import { Endpoints } from '@xver/apis';
import { Button, InputField } from '@xver/components/Atoms';
import ThaiAddressSelector, {
    ThaiAddressErrors,
    ThaiAddressValue,
    concatThaiAddress,
} from '@xver/components/Molecules/ThaiAddressSelector/ThaiAddressSelector';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { ME_QUERY_KEY } from '@xver/hooks/useMeQuery';
import { setAuthUser } from '@xver/store/auth/auth.actions';
import { getErrorMessage } from '@xver/utils/error';

import KYCSteps from './../../../assets/image/kyc_steps.jpg';

interface KYCInlineProps {
    user: User;
}

interface Level1Errors {
    firstName?: string;
    lastName?: string;
    address?: string;
    province?: string;
    district?: string;
    subDistrict?: string;
    postalCode?: string;
}

type Level1Touched = Partial<Record<keyof Level1Errors, boolean>>;

export default function KYCInline({ user }: KYCInlineProps) {
    const { t } = useTranslation('profile');

    if (user.kyc.status === KYCStatus.Pending) {
        return <PendingPanel />;
    }

    if (user.kyc.level === KYCLevel.L2 && user.kyc.status === KYCStatus.Confirmed) {
        return <CompletePanel />;
    }

    if (user.kyc.level === KYCLevel.L0) {
        return <Level1Form />;
    }

    if (user.kyc.level === KYCLevel.L1) {
        return <Level2Form />;
    }

    return <p className="text-sm text-fg/68">{t('kyc.statusConfirmed')}</p>;
}

function PendingPanel() {
    const { t } = useTranslation('profile');
    return (
        <div className="w-full flex flex-col items-center justify-center gap-3 py-8">
            <IconClock size={42} color="#f59e0b" stroke={1.5} />
            <h3 className="text-lg font-bold">{t('kyc.pending.heading')}</h3>
            <p className="text-sm text-fg/68 text-center">{t('kyc.pending.body')}</p>
        </div>
    );
}

function CompletePanel() {
    const { t } = useTranslation('profile');
    return (
        <div className="w-full flex flex-col items-center justify-center gap-3 py-8">
            <IconShieldCheck size={42} color="#22c55e" stroke={1.5} />
            <h3 className="text-lg font-bold">{t('kyc.complete.heading')}</h3>
            <p className="text-sm text-fg/68 text-center">{t('kyc.complete.body')}</p>
        </div>
    );
}

function Level1Form() {
    const { t } = useTranslation('profile');
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [street, setStreet] = useState('');
    const [address, setAddress] = useState<ThaiAddressValue>({
        province: '',
        district: '',
        subDistrict: '',
        postalCode: '',
    });
    const [agree, setAgree] = useState(false);
    const [touched, setTouched] = useState<Level1Touched>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const validation = useMemo<Level1Errors>(() => {
        const errors: Level1Errors = {};
        const fn = firstName.trim();
        const ln = lastName.trim();
        const ad = street.trim();
        if (!fn) errors.firstName = t('errors.firstNameRequired');
        else if (fn.length > 50) errors.firstName = t('errors.firstNameLength');
        if (!ln) errors.lastName = t('errors.lastNameRequired');
        else if (ln.length > 50) errors.lastName = t('errors.lastNameLength');
        if (!ad) errors.address = t('kyc.level1.addressPlaceholder');
        if (!address.province) errors.province = t('kyc.level1.provincePlaceholder');
        if (!address.district) errors.district = t('kyc.level1.districtPlaceholder');
        if (!address.subDistrict) errors.subDistrict = t('kyc.level1.subDistrictPlaceholder');
        if (address.postalCode.length !== 5) errors.postalCode = t('kyc.level1.postalCode');
        return errors;
    }, [firstName, lastName, street, address, t]);

    const visibleErrors = useMemo<Level1Errors>(() => {
        const out: Level1Errors = {};
        const showAll = submitAttempted;
        (Object.keys(validation) as Array<keyof Level1Errors>).forEach((key) => {
            if (showAll || touched[key]) out[key] = validation[key];
        });
        return out;
    }, [validation, touched, submitAttempted]);

    const addressErrors: ThaiAddressErrors = {
        province: visibleErrors.province,
        district: visibleErrors.district,
        subDistrict: visibleErrors.subDistrict,
        postalCode: visibleErrors.postalCode,
    };

    const mutation = useMutation({
        mutationFn: () =>
            Endpoints.v1.KYC.confirmKYCLevel1({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                address: concatThaiAddress(street, address),
                province: address.province,
                zipCode: address.postalCode,
            }),
        onSuccess: async (response) => {
            notifications.show({
                title: t('toast.kyc1Title'),
                message: response.message ?? t('toast.kyc1Message'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            dispatch(setAuthUser(response.data));
            await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        },
        onError: (error) => {
            notifications.show({
                title: t('toast.errorTitle'),
                message: getErrorMessage(error) ?? t('toast.errorMessage'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const handleSubmit = () => {
        if (!submitAttempted) setSubmitAttempted(true);
        if (Object.keys(validation).length > 0) return;
        if (!agree) return;
        mutation.mutate();
    };

    return (
        <div className="w-full p-4 flex flex-col gap-4">
            <div>
                <h3 className="text-lg font-bold text-blue-400 light:text-blue-700">
                    {t('kyc.level1.heading')}
                </h3>
                <p className="text-sm text-fg/68 mt-1">{t('kyc.level1.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                    size="md"
                    label={t('kyc.level1.firstName')}
                    placeholder={t('kyc.level1.firstNamePlaceholder')}
                    value={firstName}
                    onChange={(e) => {
                        setFirstName(e.target.value);
                        if (!touched.firstName) setTouched((p) => ({ ...p, firstName: true }));
                    }}
                    error={visibleErrors.firstName}
                />
                <InputField
                    size="md"
                    label={t('kyc.level1.lastName')}
                    placeholder={t('kyc.level1.lastNamePlaceholder')}
                    value={lastName}
                    onChange={(e) => {
                        setLastName(e.target.value);
                        if (!touched.lastName) setTouched((p) => ({ ...p, lastName: true }));
                    }}
                    error={visibleErrors.lastName}
                />
            </div>
            <InputField
                size="md"
                label={t('kyc.level1.address')}
                placeholder={t('kyc.level1.addressPlaceholder')}
                value={street}
                onChange={(e) => {
                    setStreet(e.target.value);
                    if (!touched.address) setTouched((p) => ({ ...p, address: true }));
                }}
                error={visibleErrors.address}
            />
            <ThaiAddressSelector
                value={address}
                onChange={(next) => {
                    setAddress(next);
                    setTouched((p) => ({
                        ...p,
                        province: true,
                        district: true,
                        subDistrict: true,
                        postalCode: true,
                    }));
                }}
                errors={addressErrors}
                labels={{
                    province: t('kyc.level1.province'),
                    provincePlaceholder: t('kyc.level1.provincePlaceholder'),
                    district: t('kyc.level1.district'),
                    districtPlaceholder: t('kyc.level1.districtPlaceholder'),
                    subDistrict: t('kyc.level1.subDistrict'),
                    subDistrictPlaceholder: t('kyc.level1.subDistrictPlaceholder'),
                    postalCode: t('kyc.level1.postalCode'),
                }}
            />
            <Checkbox
                checked={agree}
                onChange={(e) => setAgree(e.currentTarget.checked)}
                label={t('kyc.level1.agree')}
            />
            <div className="flex justify-end">
                <Button onClick={handleSubmit} disabled={!agree} isLoading={mutation.isPending}>
                    {t('kyc.level1.submit')}
                </Button>
            </div>
        </div>
    );
}

function validateAndPrepareImage(
    selected: File,
    invalidTypeMessage: string,
    onValid: (file: File) => void,
    onError: (message: string) => void
) {
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(selected.type)) {
        onError(invalidTypeMessage);
        return;
    }
    if (selected.size > 20 * 1024 * 1024) {
        new Compressor(selected, {
            quality: 0.6,
            success: (compressed) => onValid(compressed as File),
            error: () => onValid(selected),
        });
        return;
    }
    onValid(selected);
}

interface IdImageDropzoneProps {
    id: string;
    file: File | null;
    prompt: string;
    error?: string;
    onFile: (file: File) => void;
    onError: (message: string) => void;
}

function IdImageDropzone({ id, file, prompt, error, onFile, onError }: IdImageDropzoneProps) {
    const { t } = useTranslation('profile');
    const [dragging, setDragging] = useState(false);

    const handle = (selected: File) =>
        validateAndPrepareImage(selected, t('errors.uploadInvalidType'), onFile, onError);

    const onDrop = (e: DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        setDragging(false);
        if (e.dataTransfer.files?.length) handle(e.dataTransfer.files[0]);
    };

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) handle(e.target.files[0]);
    };

    return (
        <div className="flex flex-col gap-2">
            <label
                htmlFor={id}
                className={`w-full min-h-44 px-4 py-6 flex flex-col items-center justify-center gap-2 rounded-md border border-dashed cursor-pointer transition-colors ${
                    dragging ? 'border-fg/40 bg-fg/5' : 'border-fg/15 hover:border-fg/30'
                }`}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
            >
                <input
                    id={id}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    className="hidden"
                    onChange={onChange}
                />
                {file ? (
                    <Fragment>
                        <IconCheck size={28} color="#22c55e" stroke={1.5} />
                        <p className="text-sm">{file.name}</p>
                        <p className="text-xs text-fg/68">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                    </Fragment>
                ) : (
                    <Fragment>
                        <IconUpload size={28} color="#ffffff66" stroke={1.5} />
                        <p className="text-sm text-fg/85 text-center">{prompt}</p>
                        <p className="text-xs text-fg/68 text-center">
                            {t('kyc.level2.uploadHint')}
                        </p>
                    </Fragment>
                )}
            </label>
            {error && <p className="text-xs text-red-400 light:text-red-700">{error}</p>}
        </div>
    );
}

function Level2Form() {
    const { t } = useTranslation('profile');
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();

    const [active, setActive] = useState(0);
    const [frontCard, setFrontCard] = useState<File | null>(null);
    const [standardPhoto, setStandardPhoto] = useState<File | null>(null);
    const [agree, setAgree] = useState(false);
    const [error, setError] = useState<string>('');

    const stepFiles = [frontCard, standardPhoto];
    const allUploaded = Boolean(frontCard && standardPhoto);

    const mutation = useMutation({
        mutationFn: () => {
            if (!frontCard || !standardPhoto) throw new Error('no file');
            return Endpoints.v1.KYC.confirmKYCLevel2({ frontCard, standardPhoto });
        },
        onSuccess: async (response) => {
            notifications.show({
                title: t('toast.kyc2Title'),
                message: response.message ?? t('toast.kyc2Message'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            dispatch(setAuthUser(response.data));
            await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
            setFrontCard(null);
            setStandardPhoto(null);
            setAgree(false);
            setActive(0);
        },
        onError: (err) => {
            notifications.show({
                title: t('toast.errorTitle'),
                message: getErrorMessage(err) ?? t('toast.errorMessage'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const nextStep = () => {
        if (active < 2 && !stepFiles[active]) {
            setError(t('errors.uploadRequired'));
            return;
        }
        setError('');
        setActive((current) => Math.min(current + 1, 2));
    };

    const prevStep = () => {
        setError('');
        setActive((current) => Math.max(current - 1, 0));
    };

    const handleSubmit = () => {
        if (!allUploaded) {
            setError(t('errors.uploadRequired'));
            return;
        }
        if (!agree) return;
        mutation.mutate();
    };

    return (
        <div className="w-full p-4 flex flex-col gap-4">
            <div>
                <h3 className="text-lg font-bold text-blue-400 light:text-blue-700">
                    {t('kyc.level2.heading')}
                </h3>
                <p className="text-sm text-fg/68">{t('kyc.level2.subtitle')}</p>
            </div>

            <Stepper active={active} onStepClick={setActive} size="sm" allowNextStepsSelect={false}>
                <Stepper.Step label={t('kyc.level2.steps.frontCard.label')}>
                    <IdImageDropzone
                        id="kyc-front-card"
                        file={frontCard}
                        prompt={t('kyc.level2.steps.frontCard.prompt')}
                        error={!frontCard ? error : undefined}
                        onFile={(f) => {
                            setFrontCard(f);
                            setError('');
                        }}
                        onError={setError}
                    />
                </Stepper.Step>
                <Stepper.Step label={t('kyc.level2.steps.standardPhoto.label')}>
                    <div className="mb-6 w-full flex rounded-md justify-center items-center bg-stone-900">
                        <img
                            src={KYCSteps}
                            alt="KYC Sample"
                            className="my-4 h-auto w-full max-w-md"
                        />
                    </div>

                    <IdImageDropzone
                        id="kyc-standard-photo"
                        file={standardPhoto}
                        prompt={t('kyc.level2.steps.standardPhoto.prompt')}
                        error={!standardPhoto ? error : undefined}
                        onFile={(f) => {
                            setStandardPhoto(f);
                            setError('');
                        }}
                        onError={setError}
                    />
                </Stepper.Step>
                <Stepper.Completed>
                    <div className="flex flex-col gap-3 py-2">
                        <div>
                            <h4 className="text-base font-bold">
                                {t('kyc.level2.steps.confirm.heading')}
                            </h4>
                            <p className="text-sm text-fg/68">
                                {t('kyc.level2.steps.confirm.body')}
                            </p>
                        </div>
                        <h4 className="text-yellow-400 light:text-yellow-800">
                            {t('kyc.level2.steps.confirm.resetNotice')}
                        </h4>
                        <Checkbox
                            size="sm"
                            checked={agree}
                            onChange={(e) => setAgree(e.currentTarget.checked)}
                            label={t('kyc.level2.agree')}
                        />
                    </div>
                </Stepper.Completed>
            </Stepper>

            <div className="flex justify-between">
                <Button variant="secondary" onClick={prevStep} disabled={active === 0}>
                    {t('kyc.level2.back')}
                </Button>
                {active < 2 ? (
                    <Button onClick={nextStep} disabled={!stepFiles[active]}>
                        {t('kyc.level2.next')}
                    </Button>
                ) : (
                    <Button
                        onClick={handleSubmit}
                        disabled={!agree || !allUploaded}
                        isLoading={mutation.isPending}
                    >
                        {t('kyc.level2.submit')}
                    </Button>
                )}
            </div>
        </div>
    );
}
